<div class="hist-page">
  <rms-tab-group>

    <!-- ===================== Saved forecasts ===================== -->
    <rms-tab label="Saved forecasts">
      <ng-template>
        <div class="bar">
          <div class="f-type">
            <rms-select name="ehRunType" label="Type" placeholder="All types" [options]="RunTypeOptions"
                        [(ngModel)]="RunType" (ngModelChange)="loadRuns()"></rms-select>
          </div>
          <button mat-stroked-button (click)="loadRuns()" [disabled]="LoadingRuns">
            <mat-icon>refresh</mat-icon>{{ LoadingRuns ? 'Loading…' : 'Refresh' }}
          </button>
          <span class="hint">Click a name to see exactly what was saved. Each save of the same screen + filters is a new version.</span>
        </div>

        <rms-table [HeaderButtons]="RunButtons" [parentContext]="this" [data]="Runs" [columns]="RunColumns"
                   tableId="ews-saved-runs" [customizable]="true" startSort="RunID" startSortDirection="desc"></rms-table>

        <!-- ---- one saved forecast ---- -->
        @if (LoadingRun) {
        <div class="hint pad">Opening…</div>
        }
        @if (Run && !LoadingRun) {
        <div class="run">
          <div class="run-head">
            <div>
              <div class="run-title">{{ Run.RunName }}</div>
              <div class="run-sub">Run #{{ Run.RunID }} · {{ Run.GroupNames || Run.CompanyName }}</div>
            </div>
            <div class="run-actions">
              <button mat-stroked-button (click)="exportRunLines()"><mat-icon>download</mat-icon>Export lines</button>
              <button mat-stroked-button (click)="closeRun()"><mat-icon>close</mat-icon>Close</button>
            </div>
          </div>

          <div class="assumptions">
            @for (a of Assumptions; track a.label) {
            <div class="a-item"><span class="a-label">{{ a.label }}</span><span class="a-value">{{ a.value }}</span></div>
            }
          </div>

          @if (SummaryLines.length) {
          <div class="sec-title">Warehouse summary (as saved)</div>
          <table class="simple">
            <thead>
              <tr><th>Warehouse</th><th>Burn / mo</th><th>Goal / mo</th><th>In store</th><th>Reserved</th><th>Forward unsold</th><th>Status</th></tr>
            </thead>
            <tbody>
              @for (s of SummaryLines; track $index) {
              <tr>
                <td>{{ s.WarehouseText }}</td>
                <td>{{ s.BurnRate | number:'1.0-1' }}</td>
                <td>{{ s.GoalBags | number:'1.0-1' }}</td>
                <td>{{ s.OpeningBags | number:'1.0-0' }}</td>
                <td>{{ s.ReservedBags | number:'1.0-0' }}</td>
                <td>{{ s.ArrivalsUnsoldBags | number:'1.0-0' }}</td>
                <td class="left">{{ s.LineNote }}</td>
              </tr>
              }
            </tbody>
          </table>
          }

          @if (RunWarehouses.length) {
          <div class="sec-title">Month by month (as saved)</div>
          <div class="wh-chips">
            @for (w of RunWarehouses; track w.key) {
            <button type="button" class="wh-chip" [class.active]="SelectedRunWh === w.key" (click)="selectRunWh(w.key)">{{ w.name }}</button>
            }
          </div>
          <div class="grid">
            <table>
              <thead>
                <tr>
                  <th class="sticky">Month</th>
                  @for (m of MonthLines; track $index) {
                  <th>{{ m.Label }}</th> }
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="sticky">Opening bags</td>
                  @for (m of MonthLines; track $index) {
                  <td>{{ m.OpeningBags | number:'1.0-0' }}</td> }
                </tr>
                <tr>
                  <td class="sticky">+ Arriving (sold)</td>
                  @for (m of MonthLines; track $index) {
                  <td class="muted">{{ m.ArrivalsSoldBags ? (m.ArrivalsSoldBags | number:'1.0-0') : '' }}</td> }
                </tr>
                <tr>
                  <td class="sticky">+ Arriving (unsold)</td>
                  @for (m of MonthLines; track $index) {
                  <td class="muted">{{ m.ArrivalsUnsoldBags ? (m.ArrivalsUnsoldBags | number:'1.0-0') : '' }}</td> }
                </tr>
                <tr>
                  <td class="sticky">− Goal (burn + SAFS)</td>
                  @for (m of MonthLines; track $index) {
                  <td class="muted">{{ m.GoalBags | number:'1.0-1' }}</td> }
                </tr>
                <tr><td class="sticky">+ Simulated purchases</td>@for (m of MonthLines; track $index) {<td>{{ m.ProposedBags || 0 | number:'1.0-0' }}</td>}</tr>
                <tr class="closing">
                  <td class="sticky">= Goal surplus / deficit</td>
                  @for (m of MonthLines; track $index) {
                  <td [class.neg]="m.ClosingBags < 0">{{ m.ClosingBags | number:'1.0-0' }}</td>
                  }
                </tr>
              </tbody>
            </table>
          </div>
          }

          @if (OtherLines.length) {
          <div class="sec-title">Proposal / recommendation lines</div>
          <rms-table [parentContext]="this" [data]="OtherLines" [columns]="OtherColumns"
                     tableId="ews-saved-run-lines" [IncludePaginator]="false"></rms-table>
          }
        </div>
        }
      </ng-template>
    </rms-tab>

    <!-- ===================== Audit history ===================== -->
    <rms-tab label="Audit history">
      <ng-template>
        <div class="bar">
          <div class="f-date"><rms-date name="ehFrom" label="From" [(ngModel)]="AuditFrom"></rms-date></div>
          <div class="f-date"><rms-date name="ehTo" label="To" [(ngModel)]="AuditTo"></rms-date></div>
          <div class="f-type">
            <rms-select name="ehEntity" label="What" placeholder="Everything" [options]="EntityOptions"
                        [(ngModel)]="AuditEntity"></rms-select>
          </div>
          <button mat-flat-button class="btn-bg-green" (click)="loadAudit()" [disabled]="LoadingAudit">
            <mat-icon>search</mat-icon>{{ LoadingAudit ? 'Loading…' : 'Show' }}
          </button>
          <span class="hint">Before / After values are in hidden columns — turn them on with Customize, or Export CSV.</span>
        </div>

        @if (AuditLoaded) {
        <rms-table [HeaderButtons]="AuditButtons" [parentContext]="this" [data]="Audit" [columns]="AuditColumns"
                   tableId="ews-audit" [customizable]="true"></rms-table>
        } @else {
        <div class="hint pad">Pick dates and click <b>Show</b>.</div>
        }
      </ng-template>
    </rms-tab>

  </rms-tab-group>
</div>
